import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Dashboard    from './components/Dashboard';
import AuthenticatedDashboard from './components/AuthenticatedDashboard';
import Leaderboard  from './components/Leaderboard';
import UserDetail   from './components/UserDetail';

import ReferralTree from './components/ReferralTree';
import UserReport from './components/UserReport';
import UserActivityReport from './components/UserActivityReport';
import UserApproval from './components/UserApproval';
import ApproveProfileImages from './components/ApproveProfileImages';
import ApproveSocialHandles from './components/ApproveSocialHandles';
import SmsReporting from './components/SmsReporting';
import IncomingSms from './components/IncomingSms';
import BulkSms from './components/BulkSms';
import NotificationContent from './components/NotificationContent';
import AdminNotifications from './components/AdminNotifications';
import Login        from './components/Login';
import AuthError    from './components/AuthError';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { authErrors, hasPendingCreatorApprovals, hasPendingProfileImageApprovals, hasPendingSocialHandleApprovals, hasPendingRedemptions, hasUnreadIncomingSms } from './api';
import { useEnv } from './contexts/EnvContext';
import { LiveActivityProvider, useLiveActivity } from './hooks/useLiveActivity';

import AppBar   from '@mui/material/AppBar';
import Toolbar  from '@mui/material/Toolbar';
import Button   from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ListItemIcon from '@mui/material/ListItemIcon';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useThemeMode } from './index';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import TableChartIcon from '@mui/icons-material/TableChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ImageIcon from '@mui/icons-material/Image';
import SmsIcon from '@mui/icons-material/Sms';
import PaidIcon from '@mui/icons-material/Paid';
import LinkIcon from '@mui/icons-material/Link';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BuildIcon from '@mui/icons-material/Build';
import ProcessRedemptions from './components/ProcessRedemptions';
import ConnectionsReport from './components/ConnectionsReport';
import VideoBooking from './components/VideoBooking';
import VideoCases from './components/VideoCases';
import VideoCase from './components/VideoCase';
import VideoMoney from './components/VideoMoney';
import CampaignsReport from './components/CampaignsReport';
import PeriodReporting from './components/PeriodReporting';
import CreatorFunnel from './components/CreatorFunnel';
import RateLimitLogs from './components/RateLimitLogs';
import RunQuery from './components/RunQuery';
import CampaignIcon from '@mui/icons-material/Campaign';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SecurityIcon from '@mui/icons-material/Security';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { keyframes } from '@mui/system';

const drawerWidth = 240;

const approvePulse = keyframes`
  0%   { transform: scale(1); }
  50%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

function Navigation() {
  const { user, logout } = useAuth();
  const { mode, toggle } = useThemeMode();
  const { env, toggleEnv } = useEnv();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [envDialogOpen, setEnvDialogOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen(prev => !prev);
  const [needsCreatorApproval, setNeedsCreatorApproval] = React.useState(false);
  const [needsProfileImageApproval, setNeedsProfileImageApproval] = React.useState(false);
  const [needsRedemptionProcessing, setNeedsRedemptionProcessing] = React.useState(false);
  const [hasUnreadSms, setHasUnreadSms] = React.useState(false);
  const [needsSocialHandleApproval, setNeedsSocialHandleApproval] = React.useState(false);
  // Open connections, shown as a count beside the Connections link on every page.
  const { data: live } = useLiveActivity();
  const liveConnections = live?.connections?.ok ? live.connections : null;
  // Open support cases, from Bidsy's route 42 on the same poll; hidden where Bidsy has no cases yet.
  const liveCases = live?.videoCases?.ok && !live.videoCases.notDeployed ? live.videoCases : null;
  
  // Submenu expansion state with localStorage persistence
  const [openReports, setOpenReports] = React.useState(() => {
    const saved = localStorage.getItem('submenu-reports-open');
    return saved ? JSON.parse(saved) : true;
  });
  const [openUserMgmt, setOpenUserMgmt] = React.useState(() => {
    const saved = localStorage.getItem('submenu-usermgmt-open');
    return saved ? JSON.parse(saved) : true;
  });
  const [openComms, setOpenComms] = React.useState(() => {
    const saved = localStorage.getItem('submenu-comms-open');
    return saved ? JSON.parse(saved) : true;
  });
  const [openTools, setOpenTools] = React.useState(() => {
    const saved = localStorage.getItem('submenu-tools-open');
    return saved ? JSON.parse(saved) : true;
  });

  // Save submenu state to localStorage
  React.useEffect(() => {
    localStorage.setItem('submenu-reports-open', JSON.stringify(openReports));
  }, [openReports]);
  
  React.useEffect(() => {
    localStorage.setItem('submenu-usermgmt-open', JSON.stringify(openUserMgmt));
  }, [openUserMgmt]);
  
  React.useEffect(() => {
    localStorage.setItem('submenu-comms-open', JSON.stringify(openComms));
  }, [openComms]);
  
  React.useEffect(() => {
    localStorage.setItem('submenu-tools-open', JSON.stringify(openTools));
  }, [openTools]);

  // Auto-expand parent submenu when child route is active
  React.useEffect(() => {
    const path = location.pathname;
    
    // Reports submenu routes
    if (['/leaderboard', '/user-report', '/user-activity', '/connections', '/creator-funnel', '/campaigns', '/period-reporting', '/rate-limit-logs'].includes(path)) {
      setOpenReports(true);
    }
    
    // User Management submenu routes
    if (['/admin/user-approval', '/admin/picture-approval', '/admin/social-handle-approval'].includes(path)) {
      setOpenUserMgmt(true);
    }
    
    // Communications submenu routes
    if (['/sms-reporting', '/incoming-sms', '/bulk-sms', '/notification-content'].includes(path)) {
      setOpenComms(true);
    }
    
    // Tools submenu routes
    if (['/redemptions', '/admin/notifications', '/run-query'].includes(path) || path.startsWith('/video-')) {
      setOpenTools(true);
    }
  }, [location.pathname]);

  // Poll once on mount, then every 60s
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const hasAny = await hasPendingCreatorApprovals();
        if (!cancelled) setNeedsCreatorApproval(Boolean(hasAny));
      } catch {
        // fail-soft: don't spam UI on errors
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Listen for real-time updates from the Creator Approval page
  React.useEffect(() => {
    const onPending = (e) => {
      const next = Boolean(e?.detail?.hasAny);
      setNeedsCreatorApproval(next);
    };
    window.addEventListener('creator-approvals:pending', onPending);
    return () => window.removeEventListener('creator-approvals:pending', onPending);
  }, []);

  // Poll once on mount, then every 60s for pending profile images
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const hasAny = await hasPendingProfileImageApprovals();
        if (!cancelled) setNeedsProfileImageApproval(Boolean(hasAny));
      } catch {
        // fail-soft
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Listen for real-time updates from the Profile Image Approval page
  React.useEffect(() => {
    const onPending = (e) => {
      const next = Boolean(e?.detail?.hasAny);
      setNeedsProfileImageApproval(next);
    };
    window.addEventListener('profile-image-approvals:pending', onPending);
    return () => window.removeEventListener('profile-image-approvals:pending', onPending);
  }, []);
  
  // Poll once on mount, then every 60s for pending social handles
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const hasAny = await hasPendingSocialHandleApprovals();
        if (!cancelled) setNeedsSocialHandleApproval(Boolean(hasAny));
      } catch {
        // fail-soft
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Listen for real-time updates from the Social Handle Approval page
  React.useEffect(() => {
    const onPending = (e) => {
      const next = Boolean(e?.detail?.hasAny);
      setNeedsSocialHandleApproval(next);
    };
    window.addEventListener('social-handle-approvals:pending', onPending);
    return () => window.removeEventListener('social-handle-approvals:pending', onPending);
  }, []);

  // Poll once on mount, then every 60s for pending redemptions
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const hasAny = await hasPendingRedemptions();
        if (!cancelled) setNeedsRedemptionProcessing(Boolean(hasAny));
      } catch {
        // fail-soft
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Listen for real-time updates from the Redemptions page
  React.useEffect(() => {
    const onPending = (e) => {
      const next = Boolean(e?.detail?.hasAny);
      setNeedsRedemptionProcessing(next);
    };
    window.addEventListener('redemptions:pending', onPending);
    return () => window.removeEventListener('redemptions:pending', onPending);
  }, []);

  // Poll once on mount, then every 60s for unread SMS
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const hasAny = await hasUnreadIncomingSms();
        if (!cancelled) setHasUnreadSms(Boolean(hasAny));
      } catch {
        // fail-soft
      }
    };
    check();
    const id = setInterval(check, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Listen for real-time updates from the Incoming SMS page
  React.useEffect(() => {
    const onUnread = (e) => {
      const next = Boolean(e?.detail?.hasAny);
      setHasUnreadSms(next);
    };
    window.addEventListener('incoming-sms:unread', onUnread);
    return () => window.removeEventListener('incoming-sms:unread', onUnread);
  }, []);
  
  // Update document title based on env
  React.useEffect(() => {
    document.title = env === 'development' ? '[DEV] Bidsy Reporting' : 'Bidsy Reporting';
  }, [env]);

  const handleEnvToggle = () => {
    if (env === 'production') {
      setEnvDialogOpen(true);
    } else {
      toggleEnv();
    }
  };

  const confirmEnvSwitch = () => {
    setEnvDialogOpen(false);
    toggleEnv();
  };

  // If there's a whitelist error, don't show the navigation
  if (authErrors.whitelistError) {
    return null;
  }

  // Compute parent notification states
  const needsUserMgmtAttention = needsCreatorApproval || needsProfileImageApproval || needsSocialHandleApproval;
  const needsCommsAttention = hasUnreadSms;
  const needsToolsAttention = needsRedemptionProcessing;

  const drawer = (
    <Box sx={{ height: '100%' }}>
      <Toolbar>
        <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Box component="img" src="/logo.svg" alt="Bidsy" sx={{ height: 28 }} />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ '& .MuiListItemButton-root': { transition: 'background-color 0.2s', '&:hover': { bgcolor: 'action.hover' } } }}>
        
        {/* Dashboard - Top Level */}
        <ListItemButton component={NavLink} to="/" sx={{ '&.active': { bgcolor: 'action.selected' } }}>
          <ListItemIcon><DashboardIcon /></ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>

        {/* Reports Submenu */}
        <ListItemButton 
          onClick={() => setOpenReports(!openReports)}
          sx={{
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <ListItemIcon><AssessmentIcon /></ListItemIcon>
          <ListItemText primary="Reports" />
          {openReports ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openReports} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton component={NavLink} to="/leaderboard" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><EmojiEventsIcon /></ListItemIcon>
              <ListItemText primary="Referral leaderboard" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/user-report" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><TableChartIcon /></ListItemIcon>
              <ListItemText primary="User Report" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/user-activity" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><TrendingUpIcon /></ListItemIcon>
              <ListItemText primary="User Activity" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/connections" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><LinkIcon /></ListItemIcon>
              <ListItemText primary="Connections" />
              {liveConnections && (
                <Chip
                  size="small"
                  color={liveConnections.active > 0 ? 'success' : 'default'}
                  label={liveConnections.active}
                  title={`${liveConnections.active} active right now, ${liveConnections.awaitingCreator} awaiting the creator`}
                  aria-label={`${liveConnections.active} active connections`}
                />
              )}
            </ListItemButton>
            <ListItemButton component={NavLink} to="/creator-funnel" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><FilterAltIcon /></ListItemIcon>
              <ListItemText primary="Creator funnel" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/campaigns" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><CampaignIcon /></ListItemIcon>
              <ListItemText primary="Campaigns" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/period-reporting" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><BarChartIcon /></ListItemIcon>
              <ListItemText primary="Period reporting" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/rate-limit-logs" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><SecurityIcon /></ListItemIcon>
              <ListItemText primary="Rate limit logs" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* User Management Submenu */}
        <ListItemButton 
          onClick={() => setOpenUserMgmt(!openUserMgmt)}
          sx={(t) => ({
            '&:hover': { bgcolor: 'action.hover' },
            ...(needsUserMgmtAttention
              ? {
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    animation: `${approvePulse} 1.2s ease-in-out infinite`,
                    willChange: 'transform'
                  }
                }
              : {})
          })}
          title={needsUserMgmtAttention ? 'Attention needed' : undefined}
        >
          <ListItemIcon><ManageAccountsIcon /></ListItemIcon>
          <ListItemText primary="User Management" />
          {openUserMgmt ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openUserMgmt} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              component={NavLink}
              to="/admin/user-approval"
              sx={(t) => ({
                pl: 4,
                '&.active': { bgcolor: 'action.selected' },
                ...(needsCreatorApproval
                  ? {
                      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        animation: `${approvePulse} 1.2s ease-in-out infinite`,
                        willChange: 'transform'
                      }
                    }
                  : {})
              })}
              title={needsCreatorApproval ? 'Creators need approval' : undefined}
              aria-label="Approve Creators"
            >
              <ListItemIcon><ImageIcon /></ListItemIcon>
              <ListItemText primary="Approve Creators" />
            </ListItemButton>
            <ListItemButton
              component={NavLink}
              to="/admin/picture-approval"
              sx={(t) => ({
                pl: 4,
                '&.active': { bgcolor: 'action.selected' },
                ...(needsProfileImageApproval
                  ? {
                      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        animation: `${approvePulse} 1.2s ease-in-out infinite`,
                        willChange: 'transform'
                      }
                    }
                  : {})
              })}
              title={needsProfileImageApproval ? 'Pictures need approval' : undefined}
              aria-label="Approve Pictures"
            >
              <ListItemIcon><ImageIcon /></ListItemIcon>
              <ListItemText primary="Approve Pictures" />
            </ListItemButton>
            <ListItemButton
              component={NavLink}
              to="/admin/social-handle-approval"
              sx={(t) => ({
                pl: 4,
                '&.active': { bgcolor: 'action.selected' },
                ...(needsSocialHandleApproval
                  ? {
                      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        animation: `${approvePulse} 1.2s ease-in-out infinite`,
                        willChange: 'transform'
                      }
                    }
                  : {})
              })}
              title={needsSocialHandleApproval ? 'Social handles need approval' : undefined}
              aria-label="Approve Social Handles"
            >
              <ListItemIcon><AlternateEmailIcon /></ListItemIcon>
              <ListItemText primary="Approve Social Handles" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Communications Submenu */}
        <ListItemButton 
          onClick={() => setOpenComms(!openComms)}
          sx={(t) => ({
            '&:hover': { bgcolor: 'action.hover' },
            ...(needsCommsAttention
              ? {
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    animation: `${approvePulse} 1.2s ease-in-out infinite`,
                    willChange: 'transform'
                  }
                }
              : {})
          })}
          title={needsCommsAttention ? 'Attention needed' : undefined}
        >
          <ListItemIcon><SmsIcon /></ListItemIcon>
          <ListItemText primary="Communications" />
          {openComms ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openComms} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton component={NavLink} to="/sms-reporting" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><SmsIcon /></ListItemIcon>
              <ListItemText primary="SMS Reporting" />
            </ListItemButton>
            <ListItemButton
              component={NavLink}
              to="/incoming-sms"
              sx={(t) => ({
                pl: 4,
                '&.active': { bgcolor: 'action.selected' },
                ...(hasUnreadSms
                  ? {
                      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        animation: `${approvePulse} 1.2s ease-in-out infinite`,
                        willChange: 'transform'
                      }
                    }
                  : {})
              })}
              title={hasUnreadSms ? 'Unread SMS messages' : undefined}
              aria-label="Incoming SMS"
            >
              <ListItemIcon><SmsIcon /></ListItemIcon>
              <ListItemText primary="Incoming SMS" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/bulk-sms" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><SmsIcon /></ListItemIcon>
              <ListItemText primary="Bulk SMS" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/notification-content" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><EditNoteIcon /></ListItemIcon>
              <ListItemText primary="Notification content" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Tools Submenu */}
        <ListItemButton 
          onClick={() => setOpenTools(!openTools)}
          sx={(t) => ({
            '&:hover': { bgcolor: 'action.hover' },
            ...(needsToolsAttention
              ? {
                  '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    animation: `${approvePulse} 1.2s ease-in-out infinite`,
                    willChange: 'transform'
                  }
                }
              : {})
          })}
          title={needsToolsAttention ? 'Attention needed' : undefined}
        >
          <ListItemIcon><BuildIcon /></ListItemIcon>
          <ListItemText primary="Tools" />
          {openTools ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={openTools} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              component={NavLink}
              to="/redemptions"
              sx={(t) => ({
                pl: 4,
                '&.active': { bgcolor: 'action.selected' },
                ...(needsRedemptionProcessing
                  ? {
                      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        animation: `${approvePulse} 1.2s ease-in-out infinite`,
                        willChange: 'transform'
                      }
                    }
                  : {})
              })}
              title={needsRedemptionProcessing ? 'Pending redemptions' : undefined}
              aria-label="Process redemptions"
            >
              <ListItemIcon><PaidIcon /></ListItemIcon>
              <ListItemText primary="Process redemptions" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/video-cases" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><VideocamIcon /></ListItemIcon>
              <ListItemText primary="Video cases" />
              {liveCases && (
                <Chip
                  size="small"
                  color={liveCases.open > 0 ? 'warning' : 'default'}
                  label={liveCases.open}
                  title={`${liveCases.open} open video cases`}
                  aria-label={`${liveCases.open} open video cases`}
                />
              )}
            </ListItemButton>
            <ListItemButton component={NavLink} to="/video-money" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><AccountBalanceWalletIcon /></ListItemIcon>
              <ListItemText primary="Video money" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/admin/notifications" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><NotificationsIcon /></ListItemIcon>
              <ListItemText primary="Admin notifications" />
            </ListItemButton>
            <ListItemButton component={NavLink} to="/run-query" sx={{ pl: 4, '&.active': { bgcolor: 'action.selected' } }}>
              <ListItemIcon><QueryStatsIcon /></ListItemIcon>
              <ListItemText primary="Run query" />
            </ListItemButton>
          </List>
        </Collapse>
        
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`, width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` } }}>
        <Toolbar>
          {!mdUp && (
            <IconButton color="inherit" edge="start" sx={{ mr: 2 }} onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Reporting</Typography>
          {/* Env Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mr: 0.5 }}>PROD</Typography>
            <Switch
              size="small"
              checked={env === 'development'}
              onChange={handleEnvToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#E65100' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#E65100' },
              }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', ml: 0.5 }}>DEV</Typography>
            {env === 'development' && (
              <Chip label="DEVELOPMENT" size="small" sx={{ ml: 1, bgcolor: '#E65100', color: '#fff', fontWeight: 700 }} />
            )}
          </Box>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              <Button color="inherit" onClick={handleMenuOpen} startIcon={
                user.photoURL ? <Avatar src={user.photoURL} alt={user.displayName} sx={{ width: 26, height: 26 }} /> : <Avatar sx={{ width: 26, height: 26 }}>{(user.displayName || user.email || 'U').charAt(0).toUpperCase()}</Avatar>
              }>
                <Typography variant="body2" sx={{ color: 'inherit' }}>{user.displayName || user.email}</Typography>
              </Button>
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <MenuItem onClick={() => { toggle(); }}>
                  <ListItemIcon>
                    {mode === 'light' ? <Brightness4Icon fontSize="small" /> : <Brightness7Icon fontSize="small" />}
                  </ListItemIcon>
                  Toggle {mode === 'light' ? 'Dark' : 'Light'} Mode
                </MenuItem>
                <MenuItem onClick={() => { handleMenuClose(); logout(); }}>Sign Out</MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button color="inherit" component={Link} to="/login">Sign In</Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation">
        {/* Temporary drawer for mobile */}
        <Drawer
          variant="temporary"
          open={!mdUp && mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        {/* Permanent drawer for desktop */}
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Env switch confirmation dialog */}
      <Dialog open={envDialogOpen} onClose={() => setEnvDialogOpen(false)}>
        <DialogTitle>Switch to Development?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to switch to the development environment. All API requests will target the dev database and dev callback endpoints (dev.bidsy.app).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmEnvSwitch} variant="contained" sx={{ bgcolor: '#E65100', '&:hover': { bgcolor: '#BF360C' } }}>
            Switch to Dev
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function AppContent() {
  const { user, loading, authChecked } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Persist last route
  React.useEffect(() => {
    localStorage.setItem('lastRoute', location.pathname);
  }, [location.pathname]);

  // Restore last route on first load (only when at root)
  React.useEffect(() => {
    if (location.pathname === '/' && user) {
      const last = localStorage.getItem('lastRoute');
      if (last && last !== '/' && last !== '/login') {
        navigate(last, { replace: true });
      }
    }
  }, [location.pathname, user, navigate]);
  
  // Show loading state while auth is being checked
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If there's a whitelist error, show the auth error screen
  // This applies to signed-in users who aren't authorized
  if (user && authErrors.whitelistError) {
    return <AuthError />;
  }
  
  return (
    <LiveActivityProvider enabled={Boolean(user)}>
    <Box sx={{ display: 'flex' }}>
      <Navigation />
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%', mt: 8 }}>
        <Routes>
          <Route path="/" element={<AuthenticatedDashboard />} />
          <Route path="/login" element={<Login />} />
          <Route path="/leaderboard" element={
            <ProtectedRoute>
              <Leaderboard />
            </ProtectedRoute>
          } />
          <Route path="/user/:id" element={
            <ProtectedRoute>
              <UserDetail />
            </ProtectedRoute>
          } />
          <Route path="/referral-tree/:id" element={
            <ProtectedRoute>
              <ReferralTree />
            </ProtectedRoute>
          } />
          <Route path="/user-report" element={
            <ProtectedRoute>
              <UserReport />
            </ProtectedRoute>
          } />
          <Route path="/user-activity" element={
            <ProtectedRoute>
              <UserActivityReport />
            </ProtectedRoute>
          } />
          <Route path="/admin/user-approval" element={
            <ProtectedRoute>
              <UserApproval />
            </ProtectedRoute>
          } />
          <Route path="/admin/picture-approval" element={
            <ProtectedRoute>
              <ApproveProfileImages />
            </ProtectedRoute>
          } />
          <Route path="/admin/social-handle-approval" element={
            <ProtectedRoute>
              <ApproveSocialHandles />
            </ProtectedRoute>
          } />
          <Route path="/sms-reporting" element={
            <ProtectedRoute>
              <SmsReporting />
            </ProtectedRoute>
          } />
          <Route path="/incoming-sms" element={
            <ProtectedRoute>
              <IncomingSms />
            </ProtectedRoute>
          } />
          <Route path="/bulk-sms" element={
            <ProtectedRoute>
              <BulkSms />
            </ProtectedRoute>
          } />
          <Route path="/notification-content" element={
            <ProtectedRoute>
              <NotificationContent />
            </ProtectedRoute>
          } />
          <Route path="/redemptions" element={
            <ProtectedRoute>
              <ProcessRedemptions />
            </ProtectedRoute>
          } />
          <Route path="/connections" element={
            <ProtectedRoute>
              <ConnectionsReport />
            </ProtectedRoute>
          } />
          <Route path="/video-bookings/:connectionId" element={
            <ProtectedRoute>
              <VideoBooking />
            </ProtectedRoute>
          } />
          <Route path="/video-cases" element={
            <ProtectedRoute>
              <VideoCases />
            </ProtectedRoute>
          } />
          <Route path="/video-cases/:caseId" element={
            <ProtectedRoute>
              <VideoCase />
            </ProtectedRoute>
          } />
          <Route path="/video-money" element={
            <ProtectedRoute>
              <VideoMoney />
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute>
              <CampaignsReport />
            </ProtectedRoute>
          } />
          <Route path="/creator-funnel" element={
            <ProtectedRoute>
              <CreatorFunnel />
            </ProtectedRoute>
          } />
          <Route path="/period-reporting" element={
            <ProtectedRoute>
              <PeriodReporting />
            </ProtectedRoute>
          } />
          <Route path="/rate-limit-logs" element={
            <ProtectedRoute>
              <RateLimitLogs />
            </ProtectedRoute>
          } />
          <Route path="/admin/notifications" element={
            <ProtectedRoute>
              <AdminNotifications />
            </ProtectedRoute>
          } />
          <Route path="/run-query" element={
            <ProtectedRoute>
              <RunQuery />
            </ProtectedRoute>
          } />
          {/* Default redirect for unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
    </LiveActivityProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App; 